import { type ClientMessageData, useGetClientFieldsClientFieldsGetQuery } from '@shared/api';
import { useEffect, useMemo, type FC, type Dispatch, type SetStateAction } from 'react';
import { VirtualizedMessagesList } from './ui/virtualized-messages-list/virtualized-messages-list';
import { Button, Flex } from 'antd';
// import { ChatInput } from './ui/chat-input/chat-input';
import { LoadingState } from '../virtualized-dialogs-list/ui/loading-state/loading-state';
import { FetchingErrorState } from '@shared/ui/fetching-error-state/fetching-error-state';
import { EmptyState } from '../virtualized-dialogs-list/ui/empty-state/empty-state';
import type { StateSnapshot } from 'react-virtuoso';
import { ChatInput } from './ui/chat-input/chat-input';
import { useManagerTimer } from '@modules/dialogs/hooks/use-manager-timer';
import { useGetAgents } from '@shared/hooks/use-get-agents';
import styles from './chat-window.module.scss';
import { IconRightThin } from '@shared/assets/icons/components';
import classNames from 'classnames';
import { Text } from '@shared/ui/antd';
import { useTranslation } from 'react-i18next';
import type { UseGetMessagesResult } from '@modules/dialogs/hooks/use-get-messages';

interface ChatWindowProps {
  clientId: number;
  messagesState: UseGetMessagesResult;
  stateSnapshots: Map<number, StateSnapshot>;
  setStateSnapshots: Dispatch<SetStateAction<Map<number, StateSnapshot>>>;
  clientName?: string | null;
  clientAvatar?: string | null;
  isClientInfoVisible: boolean;
  showClientInfoToggle?: boolean;
  onToggleClientInfo: () => void;
  onManagerTimerChange?: (data: {
    clientId: number;
    formatted: string;
    isActive: boolean;
    canAddMore: boolean;
    addExtraTime: (() => void) | null;
    resetTimer: (() => void) | null;
  }) => void;
}

export const ChatWindow: FC<ChatWindowProps> = ({
  clientId,
  messagesState,
  stateSnapshots,
  setStateSnapshots,
  clientName,
  clientAvatar,
  isClientInfoVisible,
  showClientInfoToggle = true,
  onToggleClientInfo,
  onManagerTimerChange,
}) => {
  const { t } = useTranslation('translation');
  const handleChangeMessage: (message: ClientMessageData) => void = () => {};

  const { data: clientAgents } = useGetClientFieldsClientFieldsGetQuery(
    { clientId },
    {
      skip: !clientId,
      refetchOnMountOrArgChange: true,
    },
  );
  const { agents: organizationAgents } = useGetAgents();

  const agentNamesById = useMemo<Record<number, string>>(() => {
    const next: Record<number, string> = {};

    (organizationAgents ?? []).forEach((agent) => {
      if (typeof agent.id !== 'number') return;
      if (!agent.name?.trim()) return;

      next[agent.id] = agent.name.trim();
    });

    (clientAgents ?? []).forEach((agent) => {
      if (typeof agent.agent_id !== 'number') return;
      if (!agent.agent_name?.trim()) return;

      next[agent.agent_id] = agent.agent_name.trim();
    });

    return next;
  }, [clientAgents, organizationAgents]);

  const stateSnapshot = useMemo(() => stateSnapshots.get(clientId), [clientId, stateSnapshots]);

  const {
    messages,
    count,
    fetchNextData,
    isLoading,
    isRefetching,
    isError,
    isEmpty,
    isUninitialized,
    isFetchingMore,
    hasMore,
    handleRefetch,
  } = messagesState;

  const {
    formatted: managerTimer,
    defaultFormatted,
    hasManagerMessage,
    isActive: isManagerTimerActive,
    canAddMore: canAddManagerTime,
    addExtraTime,
    resetTimer,
  } = useManagerTimer({
    clientId,
    messages,
  });

  useEffect(() => {
    onManagerTimerChange?.({
      clientId,
      formatted: managerTimer,
      isActive: isManagerTimerActive,
      canAddMore: canAddManagerTime,
      addExtraTime,
      resetTimer,
    });
  }, [
    clientId,
    managerTimer,
    isManagerTimerActive,
    canAddManagerTime,
    addExtraTime,
    resetTimer,
    onManagerTimerChange,
  ]);

  return (
    <Flex gap={12} flex={1} vertical key={clientId} className={styles.root}>
      <Flex align="center" justify="space-between" className={styles.header}>
        <Flex align="center" gap={8} className={styles.headerLeft}>
          <Flex align="center" gap={6} className={styles.timerGroup}>
            <Text
              variant="sm"
              fontWeight={400}
              className={classNames(styles.timer, {
                [styles.timerInactive]: !hasManagerMessage,
              })}
            >
              {t('dialogs.chatWindow.time')}:&nbsp;
              <Text
                variant="sm"
                fontWeight={400}
                className={classNames(styles.timerValue, {
                  [styles.timerValueInactive]: !hasManagerMessage,
                })}
              >
                {hasManagerMessage ? managerTimer : defaultFormatted}
              </Text>
            </Text>

            {isManagerTimerActive ? (
              <>
                <Button
                  onClick={() => addExtraTime?.()}
                  disabled={!canAddManagerTime}
                  className={styles.timerAddButton}
                >
                  {t('dialogs.chatWindow.addTime')}
                </Button>

                <Button
                  onClick={() => resetTimer?.()}
                  disabled={!resetTimer}
                  className={styles.timerEndButton}
                >
                  {t('dialogs.chatWindow.finish')}
                </Button>
              </>
            ) : null}
          </Flex>
        </Flex>

        {showClientInfoToggle ? (
          <Flex className={styles.headerRight}>
            <Button
              icon={
                <IconRightThin
                  className={classNames(styles.toggleIcon, {
                    [styles.toggleIcon_rotated]: !isClientInfoVisible,
                  })}
                />
              }
              type="text"
              variant="outlined"
              onClick={onToggleClientInfo}
              className={styles.toggleButton}
            />
          </Flex>
        ) : null}
      </Flex>

      {(() => {
        const hasMessages = Boolean(messages?.length);

        if (clientId)
          if (!hasMessages && (isLoading || isRefetching || isUninitialized)) {
            return <LoadingState />;
          }

        if (isError) {
          return <FetchingErrorState refetch={handleRefetch} />;
        }

        if (isEmpty) {
          return <EmptyState />;
        }

        return (
          <VirtualizedMessagesList
            messages={messages || []}
            clientId={clientId}
            totalCount={count}
            clientName={clientName}
            clientAvatar={clientAvatar}
            agentNamesById={agentNamesById}
            switchNoticePrefix={t('dialogs.chat.switchNoticePrefix')}
            switchNoticeTooltip={t('dialogs.chat.switchNoticeTooltip')}
            aiAgentLabel={t('dialogs.chat.aiAgent')}
            stateSnapshot={stateSnapshot}
            isFetchingMore={isFetchingMore}
            hasMore={hasMore}
            fetchNextData={fetchNextData}
            onChangeMessage={handleChangeMessage}
            onStateSnapshotChange={({ state, clientId }) => {
              setStateSnapshots((prev) => {
                const next = new Map(prev);
                next.set(clientId, state);
                return next;
              });
            }}
          />
        );
      })()}

      <ChatInput clientId={clientId} />
    </Flex>
  );
};
